import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  alpha,
  IconButton,
  Avatar,
} from '@mui/material'
import { useState, useEffect, useMemo, useRef } from 'react'
import { toast } from 'react-toastify'
import Image from '@mui/icons-material/Image'
import PhotoLibrary from '@mui/icons-material/PhotoLibrary'
import CloudUpload from '@mui/icons-material/CloudUpload'
import Close from '@mui/icons-material/Close'
import { getCPDataWithoutParams, postCPData } from 'src/clinic/services'
import { postDataWithoutParams } from 'src/consumer/services/featureServices'
import { mediaURL } from 'src/consumer/services'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import {
  CLINIC_EVENT_TYPES_FOR_SERVICES,
  CLINIC_SERVICES_ITEMS_TYPES,
  currency_list,
  EVENT_MODES_ARRAY,
  VISIT_EVENT_TYPES_ARRAY,
} from 'src/consumer/constants'
import { genericHandleNestedFields } from 'src/consumer/helpers/utilFunctions'
import {
  StyledEventInputContainer,
  StyledEventReactSelect,
} from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'
import { CountryPricingContainer } from 'src/mentor/components/MentorEventsPage/MentorEventsPageStyles'
import { getDataMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'
import {
  buildScreeningTargetSpecialtyOptions,
  filterScreeningTargetSpecialtyOptionsForClinic,
  formatScreeningSpecialtyLabel,
  normalizeScreeningSpecialtyBucket,
  normalizeScreeningSpecialtyForPayload,
} from 'src/consumer/helpers/screeningSpecialtyUtils'
import { getSupportClinicApptTypeSaveError } from './supportClinicAppointmentTypeSaveErrors'

/**
 * Build payload for POST /api/secure/cc/support/clinic/clinic-appointment-type/save.
 *
 * Location: src/clinic/views/clinicServices/components/ClinicAppointmentTypeForm.jsx
 * Function: buildSupportClinicAppointmentTypeSavePayload()
 * Called from: saveAppointmentType() in the same file when saveAppointmentApiSignature includes /api/secure/cc/support/.
 *
 * Contract: clinicBrandId, clinicLocationId, type, mode, itemsType, duration (number mins), pricingDetail (currencyId + price fields as numbers);
 * optional title, templateId, visitType/visitEventType, clinicalAssessmentIds, specializationsMentorIds (practitioner IDs e.g. TCH-...), specializationType (incl/excl).
 * Do not send mentorId — it is not part of the request contract; practitioner targeting is via specializationsMentorIds only.
 *
 * 1) templateId: set only when a default template is explicitly selected from /default-templates/list (validDefaultTemplateIds).
 *    Otherwise omit templateId (do not send templateId: null in payload; field is absent).
 *
 * 2) Map selected clinical assessment IDs only to clinicalAssessmentIds (array); never to templateId.
 *
 * 3) Guard: if templateId equals any value in clinicalAssessmentIds and is NOT in validDefaultTemplateIds, log warning and omit templateId. If it is in validDefaultTemplateIds, send it (same ID may refer to both template and assessment in your data).
 *
 * @param {Set<string>|string[]|null|undefined} validDefaultTemplateIds - IDs from GET /api/secure/cc/support/clinic/default-templates/list.
 */
function buildSupportClinicAppointmentTypeSavePayload(formData, clinicId, clinicBrandId, existingItem, validDefaultTemplateIds) {
  const duration =
    formData.duration !== '' && formData.duration != null ? Number(formData.duration) : undefined
  const clinicalAssessmentIds =
    Array.isArray(formData.clinicalAssessmentIds) && formData.clinicalAssessmentIds.length > 0
      ? formData.clinicalAssessmentIds
      : formData.psychAssessmentIds ?? undefined
  const normalizedTargetSpecialtySlug = normalizeScreeningSpecialtyForPayload(
    formData.targetSpecialtySlug,
  )

  const payload = {
    clinicBrandId,
    clinicLocationId: clinicId,
    type: formData.type,
    mode: formData.mode,
    itemsType: formData.itemsType,
    duration,
  }
  if (formData.title != null && formData.title !== '') payload.title = formData.title

  if (existingItem?.id) payload.id = existingItem.id

  const templateIdRaw = formData.templateId != null && formData.templateId !== '' ? formData.templateId : null
  const validSet = validDefaultTemplateIds != null
    ? (validDefaultTemplateIds instanceof Set ? validDefaultTemplateIds : new Set(validDefaultTemplateIds))
    : null
  const assessmentIdSet = new Set(
    (clinicalAssessmentIds || []).map((id) => String(id)),
  )
  const isInValidSet = validSet != null && (validSet.has(templateIdRaw) || validSet.has(String(templateIdRaw)))
  const templateIdAlsoInAssessments = templateIdRaw && assessmentIdSet.has(String(templateIdRaw))
  if (templateIdAlsoInAssessments && !isInValidSet) {
    console.warn(
      '[clinic-appointment-type/save] templateId matched a clinicalAssessmentId and is not in default-templates list; omitting templateId. templateId=',
      templateIdRaw,
      'clinicalAssessmentIds=',
      clinicalAssessmentIds,
    )
  }
  const isValidTemplateId = templateIdRaw && (validSet == null || isInValidSet)
  if (isValidTemplateId) {
    payload.templateId = templateIdRaw
  }

  if (formData.visitType != null && formData.visitType !== '') payload.visitType = formData.visitType
  if (clinicalAssessmentIds != null && clinicalAssessmentIds.length > 0) payload.clinicalAssessmentIds = clinicalAssessmentIds
  if (formData.type === SCREENING_EVENT_TYPE && normalizedTargetSpecialtySlug) {
    payload.targetSpecialtySlug = normalizedTargetSpecialtySlug
  }

  // Practitioner targeting: only specializationsMentorIds (array of IDs e.g. TCH-...). Do not send mentorId.
  const ids =
    Array.isArray(formData.specializationsMentorIds) && formData.specializationsMentorIds.length > 0
      ? formData.specializationsMentorIds
      : [formData.mentorId ?? existingItem?.mentorId ?? existingItem?.mentor_id].filter(Boolean)
  if (ids.length > 0) payload.specializationsMentorIds = ids.map((id) => String(id))
  if (formData.specializationType != null && formData.specializationType !== '') payload.specializationType = formData.specializationType
  if (formData.overridesClinicAppointmentTypeId != null && formData.overridesClinicAppointmentTypeId !== '') {
    payload.overridesClinicAppointmentTypeId = formData.overridesClinicAppointmentTypeId
  }

  const pd = formData.pricingDetail
  if (pd && typeof pd === 'object') {
    payload.pricingDetail = {
      ...(pd.currencyId != null && pd.currencyId !== '' && { currencyId: String(pd.currencyId) }),
      ...(pd.price != null && pd.price !== '' && { price: Number(pd.price) }),
      ...(pd.discountedPrice != null && pd.discountedPrice !== '' && { discountedPrice: Number(pd.discountedPrice) }),
      ...(pd.introductoryPrice != null && pd.introductoryPrice !== '' && { introductoryPrice: Number(pd.introductoryPrice) }),
      ...(pd.vatPercentage != null && pd.vatPercentage !== '' && { vatPercentage: Number(pd.vatPercentage) }),
    }
    if (Object.keys(payload.pricingDetail).length === 0) delete payload.pricingDetail
  }

  return payload
}

export const ITEMS_TYPE_OPTIONS = [
  { label: 'Single', value: 'single' },
  { label: 'Multiple', value: 'bundle' },
]

const PRICING_SOURCE_TIER1 = 'tier1'
const PRICING_SOURCE_TIER2 = 'tier2'
const PRICING_SOURCE_TIER3 = 'tier3'
const PRICING_SOURCE_CUSTOM = 'custom'
const SCREENING_EVENT_TYPE = 'screening'

function isNumericValue(v) {
  if (v == null) return false
  if (typeof v === 'number' && !Number.isNaN(v)) return true
  const n = parseFloat(v)
  return !Number.isNaN(n)
}

function buildTargetSpecialtyOptionsFromResponse(rawTargetSpecialties) {
  return buildScreeningTargetSpecialtyOptions(rawTargetSpecialties)
}

/**
 * Get list of non-null pricing tiers from template.pricingMatrix.
 * pricingMatrix shape: { ROW: { tier1: null, tier2: { currencyId, price, ... }, tier3: null } }
 * or key from template.pricingCountry (e.g. "row" -> match "ROW").
 * @returns {{ key: string, label: string, data: object }[]}
 */
function getTemplatePricingTiers(template) {
  if (!template?.pricingMatrix || typeof template.pricingMatrix !== 'object') return []
  const matrix = template.pricingMatrix
  const keys = Object.keys(matrix)
  const countryKey =
    (template.pricingCountry && keys.find((k) => String(k).toLowerCase() === String(template.pricingCountry).toLowerCase())) ||
    (keys.includes('ROW') && 'ROW') ||
    keys[0]
  if (!countryKey) return []
  const countryTiers = matrix[countryKey]
  if (!countryTiers || typeof countryTiers !== 'object') return []
  const out = []
  ;['tier1', 'tier2', 'tier3'].forEach((key) => {
    const t = countryTiers[key]
    if (t && typeof t === 'object' && (isNumericValue(t.price) || isNumericValue(t.discountedPrice) || isNumericValue(t.introductoryPrice))) {
      out.push({ key, label: `Tier ${key.slice(-1)}`, data: t })
    }
  })
  return out
}

const EVENT_TYPE_CONSULTATION = 'consultation'
const EVENT_TYPES_THERAPIST = ['therapy-session', 'screening', 'assessment']

/**
 * Support clinic: GET .../clinical-assessment/list with ids (repeatable or comma-separated).
 * Response: CommonResponse with data.clinicalAssessments (array of ClinicalAssessment).
 * languageCode, platform, countryCode are added by getSPDataWithParams.
 */
const DEFAULT_CLINICAL_ASSESSMENTS_LIST_SUPPORT =
  '/api/secure/cc/support/clinic/clinical-assessment/list'
const DEFAULT_CLINICAL_ASSESSMENTS_LIST_CLINIC =
  '/api/secure/clinic-practitioner/psych-assessment/list'

/** Predefined icons for "Choose from gallery". Each: { id: fileId, label }. Add default icon fileIds here if available. */
export const SERVICE_ICON_GALLERY = []

export default function ClinicAppointmentTypeForm({
  clinicAppointmentType,
  defaultTemplates,
  clinicLocation,
  clinicBrand,
  closeDialog,
  refreshData,
  hideAppointmentType = false,
  hideVisitEventType = false,
  hideMode = false,
  clinicId,
  clinicBrandId,
  isDrawerMode = false,
  saveAppointmentApiSignature,
  /** Optional. GET with ids=... returns array of { id, title? }. Used to show assessment titles instead of ids. */
  clinicalAssessmentsListApiSignature,
}) {
  const [assessmentTitlesMap, setAssessmentTitlesMap] = useState({})
  const [formData, setFormData] = useState(() => {
    const existing = clinicAppointmentType || {}
    const mentorIds =
      Array.isArray(existing.specializationsMentorIds) && existing.specializationsMentorIds.length > 0
        ? existing.specializationsMentorIds
        : (existing.mentorId || existing.mentor_id ? [existing.mentorId || existing.mentor_id] : [])
    const assessmentIds = existing.clinicalAssessmentIds ?? existing.psychAssessmentIds ?? []
    const rawTid = existing.templateId != null && existing.templateId !== '' ? existing.templateId : ''
    const isAssessmentId = Array.isArray(assessmentIds) && assessmentIds.some((id) => String(id) === String(rawTid))
    const templateId = rawTid && !isAssessmentId ? rawTid : ''
    return {
      ...existing,
      title: existing.title || '',
      itemsType: existing.itemsType || '',
      type: existing.type || '',
      visitType: existing.visitType || '',
      mode: existing.mode || '',
      targetSpecialtySlug: normalizeScreeningSpecialtyBucket(
        existing.targetSpecialtySlug ?? existing.screeningType ?? '',
      ),
      mentorId: existing.mentorId ?? existing.mentor_id ?? '',
      specializationsMentorIds: mentorIds,
      templateId,
      pricingDetail: existing.pricingDetail || {},
      duration: existing.duration ?? existing.durationInMins ?? '',
      clinicalAssessmentIds: Array.isArray(assessmentIds) ? assessmentIds : [],
      iconId: existing.iconId ?? existing.icon ?? '',
    }
  })

  const [saving, setSaving] = useState(false)
  const [pricingSource, setPricingSource] = useState(PRICING_SOURCE_CUSTOM)
  const [doctorsForLocation, setDoctorsForLocation] = useState([])
  const [therapistsForLocation, setTherapistsForLocation] = useState([])
  const [targetSpecialties, setTargetSpecialties] = useState([])
  const [loadingPractitioners, setLoadingPractitioners] = useState(false)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [iconUploading, setIconUploading] = useState(false)
  const iconFileInputRef = useRef(null)

  const hasTemplateId = Boolean(formData.templateId)
  const defaultTemplate = useMemo(
    () =>
      (defaultTemplates || []).find((t) => t?.id === formData.templateId) || null,
    [defaultTemplates, formData.templateId],
  )
  const templatePricingTiers = useMemo(
    () => getTemplatePricingTiers(defaultTemplate),
    [defaultTemplate],
  )
  const showPricingSourceSelector = Boolean(hasTemplateId && defaultTemplate && templatePricingTiers.length > 0)
  const screeningTargetSpecialtyOptions = useMemo(
    () =>
      filterScreeningTargetSpecialtyOptionsForClinic(
        buildTargetSpecialtyOptionsFromResponse(targetSpecialties),
        clinicLocation,
        clinicBrand,
      ),
    [targetSpecialties, clinicLocation, clinicBrand],
  )

  const isSupportPortal = Boolean(saveAppointmentApiSignature?.includes('/api/secure/cc/support/'))

  // Fetch doctors and therapists for this clinic location (support portal only)
  useEffect(() => {
    if (!isSupportPortal || !clinicLocation?.id) {
      setDoctorsForLocation([])
      setTherapistsForLocation([])
      return
    }
    const doctorUids = (clinicLocation.doctors || []).map((d) => d?.uid ?? d?.id).filter(Boolean)
    const therapistUids = (clinicLocation.therapists || []).map((t) => t?.uid ?? t?.id).filter(Boolean)
    if (doctorUids.length === 0 && therapistUids.length === 0) {
      setDoctorsForLocation([])
      setTherapistsForLocation([])
      return
    }
    let cancelled = false
    setLoadingPractitioners(true)
    Promise.all([
      getSPDataWithParams('/api/secure/cc/support/doctor/list', {}),
      getSPDataWithParams('/api/secure/cc/support/therapists/clinical/list', {}),
    ])
      .then(([docResp, thResp]) => {
        if (cancelled) return
        const docList = (docResp && isCommonResponseSuccessfulV2(docResp) && getDataMapFromCommonResponse(docResp)?.mentor) || []
        const thList = (thResp && isCommonResponseSuccessfulV2(thResp) && getDataMapFromCommonResponse(thResp)?.mentor) || []
        const doctors = docList.filter((m) => doctorUids.some((uid) => uid === m?.id || uid === m?.uid || uid === String(m?.id) || uid === String(m?.uid)))
        const therapists = thList.filter((m) => therapistUids.some((uid) => uid === m?.id || uid === m?.uid || uid === String(m?.id) || uid === String(m?.uid)))
        setDoctorsForLocation((doctors || []).sort((a, b) => (a?.name || a?.title || '').localeCompare(b?.name || b?.title || '')))
        setTherapistsForLocation((therapists || []).sort((a, b) => (a?.name || a?.title || '').localeCompare(b?.name || b?.title || '')))
      })
      .catch(() => {
        if (!cancelled) {
          setDoctorsForLocation([])
          setTherapistsForLocation([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPractitioners(false)
      })
    return () => {
      cancelled = true
    }
  }, [isSupportPortal, clinicLocation?.id, clinicLocation?.doctors, clinicLocation?.therapists])

  // Update form data when clinicAppointmentType changes (for drawer mode)
  const validDefaultTemplateIdSet = useMemo(
    () => new Set((defaultTemplates || []).map((t) => t?.id).filter(Boolean)),
    [defaultTemplates],
  )
  useEffect(() => {
    if (clinicAppointmentType) {
      const existing = clinicAppointmentType
      const mentorIds =
        Array.isArray(existing.specializationsMentorIds) && existing.specializationsMentorIds.length > 0
          ? existing.specializationsMentorIds
          : (existing.mentorId || existing.mentor_id ? [existing.mentorId || existing.mentor_id] : [])
      const rawTemplateId = existing.templateId != null && existing.templateId !== '' ? existing.templateId : ''
      const templateId =
        rawTemplateId && validDefaultTemplateIdSet.has(rawTemplateId)
          ? rawTemplateId
          : validDefaultTemplateIdSet.has(String(rawTemplateId))
            ? String(rawTemplateId)
            : ''
      setFormData({
        ...(existing || {}),
        title: existing.title || '',
        itemsType: existing.itemsType || '',
        type: existing.type || '',
        visitType: existing.visitType || '',
        mode: existing.mode || '',
        targetSpecialtySlug: normalizeScreeningSpecialtyBucket(
          existing.targetSpecialtySlug ?? existing.screeningType ?? '',
        ),
        mentorId: existing.mentorId ?? existing.mentor_id ?? '',
        specializationsMentorIds: mentorIds,
        templateId,
        pricingDetail: existing.pricingDetail || {},
        duration: existing.duration ?? existing.durationInMins ?? '',
        clinicalAssessmentIds:
          existing.clinicalAssessmentIds ?? existing.psychAssessmentIds ?? [],
        iconId: existing.iconId ?? existing.icon ?? '',
      })
    }
  }, [clinicAppointmentType, validDefaultTemplateIdSet])

  useEffect(() => {
    if (formData.type !== SCREENING_EVENT_TYPE || screeningTargetSpecialtyOptions.length === 0) {
      return
    }
    const currentBucket = normalizeScreeningSpecialtyBucket(formData.targetSpecialtySlug)
    const isCurrentAllowed = screeningTargetSpecialtyOptions.some(
      (option) => normalizeScreeningSpecialtyBucket(option?.slug) === currentBucket,
    )
    if (isCurrentAllowed) {
      return
    }
    setFormData((prev) => ({
      ...prev,
      targetSpecialtySlug: normalizeScreeningSpecialtyBucket(
        screeningTargetSpecialtyOptions[0]?.slug,
      ),
    }))
  }, [formData.type, formData.targetSpecialtySlug, screeningTargetSpecialtyOptions])

  useEffect(() => {
    let cancelled = false

    async function loadTargetSpecialties() {
      try {
        if (isSupportPortal) {
          const resp = await getSPDataWithParams(
            '/api/secure/cc/support/target-specialties',
            { includeDisabled: false, sortBy: 'title' },
            undefined,
            undefined,
            true,
          )
          if (!cancelled) {
            setTargetSpecialties(Array.isArray(resp?.data) ? resp.data : [])
          }
          return
        }

        const resp = await getCPDataWithoutParams(
          '/api/secure/clinic-appointment/options/target-specialties?includeDisabled=false&sortBy=title',
        )
        if (!cancelled) {
          if (isCommonResponseSuccessfulV2(resp)) {
            const data = getDataMapFromCommonResponse(resp)
            setTargetSpecialties(data?.targetSpecialties || [])
          } else {
            setTargetSpecialties([])
          }
        }
      } catch (error) {
        if (!cancelled) {
          setTargetSpecialties([])
        }
      }
    }

    loadTargetSpecialties()

    return () => {
      cancelled = true
    }
  }, [isSupportPortal])

  // Resolve clinical assessment ids to titles for display (drawer and template section)
  const assessmentIdsToResolve = useMemo(() => {
    const ids =
      formData.clinicalAssessmentIds ??
      formData.psychAssessmentIds ??
      clinicAppointmentType?.clinicalAssessmentIds ??
      clinicAppointmentType?.psychAssessmentIds ??
      defaultTemplate?.clinicalAssessmentIds ??
      defaultTemplate?.psychAssessmentIds ??
      []
    return Array.isArray(ids) ? ids.filter(Boolean) : []
  }, [
    formData.clinicalAssessmentIds,
    formData.psychAssessmentIds,
    clinicAppointmentType?.clinicalAssessmentIds,
    clinicAppointmentType?.psychAssessmentIds,
    defaultTemplate?.clinicalAssessmentIds,
    defaultTemplate?.psychAssessmentIds,
  ])

  useEffect(() => {
    if (assessmentIdsToResolve.length === 0) {
      setAssessmentTitlesMap({})
      return
    }
    const apiSignature =
      clinicalAssessmentsListApiSignature ??
      (isSupportPortal ? DEFAULT_CLINICAL_ASSESSMENTS_LIST_SUPPORT : null) ??
      (clinicId && clinicBrandId ? DEFAULT_CLINICAL_ASSESSMENTS_LIST_CLINIC : null)
    if (!apiSignature) {
      setAssessmentTitlesMap({})
      return
    }
    let cancelled = false
    const idsParam = assessmentIdsToResolve.join(',')

    if (isSupportPortal) {
      // Support API: ids as repeatable (ids=id1&ids=id2); languageCode, platform, countryCode added by getSPDataWithParams
      const params = { ids: assessmentIdsToResolve }
      getSPDataWithParams(apiSignature, params)
        .then((res) => {
          if (cancelled || res?.status !== 200) return
          const data = res?.data?.data ?? res?.data
          const list = Array.isArray(data?.clinicalAssessments)
            ? data.clinicalAssessments
            : Array.isArray(data) ? data : data?.list ?? []
          const map = {}
          list.forEach((a) => {
            if (a?.id) {
              map[a.id] =
                a.title ?? a.name ?? a.slug ?? a.label ?? String(a.id)
            }
          })
          setAssessmentTitlesMap((prev) => ({ ...prev, ...map }))
        })
        .catch(() => {
          if (!cancelled) setAssessmentTitlesMap({})
        })
    } else {
      const url =
        apiSignature +
        (apiSignature.includes('?') ? '&' : '?') +
        `ids=${encodeURIComponent(idsParam)}` +
        (clinicId ? `&clinicId=${encodeURIComponent(clinicId)}` : '') +
        (clinicBrandId ? `&clinicBrandId=${encodeURIComponent(clinicBrandId)}` : '')
      getCPDataWithoutParams(url)
        .then((res) => {
          if (cancelled || res?.status !== 200) return
          const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? res?.data?.list ?? []
          const map = {}
          list.forEach((a) => {
            if (a?.id) {
              map[a.id] =
                a.title ?? a.name ?? a.slug ?? a.label ?? String(a.id)
            }
          })
          setAssessmentTitlesMap((prev) => ({ ...prev, ...map }))
        })
        .catch(() => {
          if (!cancelled) setAssessmentTitlesMap({})
        })
    }
    return () => {
      cancelled = true
    }
  }, [
    assessmentIdsToResolve.join(','),
    clinicalAssessmentsListApiSignature,
    isSupportPortal,
    clinicId,
    clinicBrandId,
  ])

  const hasInitializedPricingSource = useRef(false)
  useEffect(() => {
    hasInitializedPricingSource.current = false
    if (!hasTemplateId) {
      setPricingSource(PRICING_SOURCE_CUSTOM)
    }
  }, [hasTemplateId, formData.templateId])
  // When we have template pricing tiers, infer initial pricing source once from current pricingDetail
  useEffect(() => {
    if (!showPricingSourceSelector || templatePricingTiers.length === 0 || hasInitializedPricingSource.current) return
    hasInitializedPricingSource.current = true
    const current = clinicAppointmentType?.pricingDetail
    const sameNum = (a, b) => a != null && b != null && Number(a) === Number(b)
    const match = templatePricingTiers.find(
      (t) =>
        current?.currencyId === t.data?.currencyId &&
        (sameNum(t.data?.price, current?.price) || sameNum(t.data?.discountedPrice, current?.price) || sameNum(t.data?.introductoryPrice, current?.price)),
    )
    if (match) setPricingSource(match.key)
    else setPricingSource(PRICING_SOURCE_CUSTOM)
  }, [showPricingSourceSelector, templatePricingTiers, clinicAppointmentType?.pricingDetail])

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'type') {
        const isConsultation = value === EVENT_TYPE_CONSULTATION
        const isTherapistType = EVENT_TYPES_THERAPIST.includes(value)
        const options = isConsultation ? doctorsForLocation : isTherapistType ? therapistsForLocation : therapistsForLocation
        const allowedIds = (options || []).map((p) => p?.id || p?.uid).filter(Boolean)
        const allowedSet = new Set([...allowedIds.map(String), ...allowedIds])
        const currentIds = Array.isArray(prev.specializationsMentorIds) ? prev.specializationsMentorIds : (prev.mentorId ? [prev.mentorId] : [])
        next.specializationsMentorIds = currentIds.filter((id) => allowedSet.has(String(id)))
        if (prev.mentorId && !allowedIds.includes(prev.mentorId) && !allowedIds.includes(String(prev.mentorId))) {
          next.mentorId = ''
        }
        if (value !== SCREENING_EVENT_TYPE) {
          next.targetSpecialtySlug = ''
        } else {
          next.targetSpecialtySlug = normalizeScreeningSpecialtyBucket(prev.targetSpecialtySlug)
        }
      }
      return next
    })
  }

  const serviceOwnerOptions = useMemo(() => {
    const type = formData.type
    if (type === EVENT_TYPE_CONSULTATION) return doctorsForLocation
    if (EVENT_TYPES_THERAPIST.includes(type)) return therapistsForLocation
    return therapistsForLocation
  }, [formData.type, doctorsForLocation, therapistsForLocation])

  const showServiceOwnerDropdown = isSupportPortal && (doctorsForLocation.length > 0 || therapistsForLocation.length > 0)

  function showSaveError(code, serverMessage) {
    const isSupportApi = saveAppointmentApiSignature?.includes('/api/secure/cc/support/')
    if (isSupportApi && code != null) {
      const { message, action } = getSupportClinicApptTypeSaveError(code)
      toast.error(action ? `${message} ${action}` : message, { autoClose: 6000 })
    } else {
      toast.error(serverMessage || 'Failed to save. Please try again.', { autoClose: 5000 })
    }
  }

  async function saveAppointmentType() {
    setSaving(true)

    const formDataToSave = {
      ...formData,
      clinicLocationId: clinicId,
      clinicBrandId: clinicBrandId,
      duration:
        formData.duration !== '' && formData.duration != null
          ? Number(formData.duration)
          : undefined,
      clinicalAssessmentIds:
        Array.isArray(formData.clinicalAssessmentIds) && formData.clinicalAssessmentIds.length > 0
          ? formData.clinicalAssessmentIds
          : formData.psychAssessmentIds ?? undefined,
    }
    const normalizedTargetSpecialtySlug = normalizeScreeningSpecialtyForPayload(
      formDataToSave.targetSpecialtySlug,
    )
    if (formDataToSave.type === SCREENING_EVENT_TYPE && normalizedTargetSpecialtySlug) {
      formDataToSave.targetSpecialtySlug = normalizedTargetSpecialtySlug
    } else {
      delete formDataToSave.targetSpecialtySlug
    }

    // When a template tier is selected, set pricingDetail from that tier
    if (showPricingSourceSelector && templatePricingTiers.length > 0 && pricingSource !== PRICING_SOURCE_CUSTOM) {
      const tier = templatePricingTiers.find((t) => t.key === pricingSource)
      if (tier?.data) {
        const pd = tier.data
        formDataToSave.pricingDetail = {
          currencyId: pd.currencyId || formData.pricingDetail?.currencyId,
          vatPercentage: pd.vatPercentage ?? formData.pricingDetail?.vatPercentage,
          price: pd.price,
          discountedPrice: pd.discountedPrice ?? pd.price,
          introductoryPrice: pd.introductoryPrice ?? pd.price,
        }
      }
    }

    // Include ID if editing existing item
    if (clinicAppointmentType?.id) {
      formDataToSave.id = clinicAppointmentType.id
    }
    if (!saveAppointmentApiSignature?.includes('/api/secure/cc/support/')) {
      if (formData.iconId) formDataToSave.iconId = formData.iconId
      // Non-support API may expect single mentorId; pass first selected owner
      const firstId = formDataToSave.specializationsMentorIds?.[0]
      if (firstId != null) formDataToSave.mentorId = firstId
    }

    const isSupportPortalAPI = saveAppointmentApiSignature?.includes('/api/secure/cc/support/')
    const apiUrl = saveAppointmentApiSignature || '/api/secure/clinic-appointment/save'
    const validDefaultTemplateIds =
      (defaultTemplates || []).map((t) => t?.id).filter(Boolean)
    const payloadToSend = isSupportPortalAPI
      ? buildSupportClinicAppointmentTypeSavePayload(
          formDataToSave,
          clinicId,
          clinicBrandId,
          clinicAppointmentType,
          validDefaultTemplateIds,
        )
      : formDataToSave

    try {
      const resp = isSupportPortalAPI
        ? await postSPDataWithParams(apiUrl, payloadToSend, {}, undefined)
        : await postCPData(apiUrl, payloadToSend)

      // Support portal postSPDataWithParams returns err on failure instead of throwing
      const isAxiosError = resp && !resp?.status && resp?.response
      const errorPayload = isAxiosError ? resp.response?.data : resp?.data
      const status = isAxiosError ? resp.response?.status : resp?.status

      if (status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          if (typeof refreshData === 'function') {
            const maybePromise = refreshData()
            if (maybePromise && typeof maybePromise.then === 'function') {
              await maybePromise
            }
          }
          closeDialog?.()
        } else {
          const code = errorPayload?.code ?? resp?.data?.code
          const msg = errorPayload?.message ?? resp?.data?.message
          showSaveError(code, msg)
        }
      } else if (errorPayload) {
        showSaveError(errorPayload?.code, errorPayload?.message)
      } else {
        showSaveError(null, 'Request failed. Please try again.')
      }
    } catch (err) {
      const code = err?.response?.data?.code
      const msg = err?.response?.data?.message
      showSaveError(code, msg)
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = () => {
    if (!formData.title || !formData.itemsType) {
      return
    }
    saveAppointmentType()
  }

  function handleChangePricing(e) {
    genericHandleNestedFields(e, 'pricingDetail', setFormData)
  }

  async function handleIconFileChange(e) {
    const file = e.target?.files?.[0]
    if (!file) return
    if (!/^image\//.test(file.type)) {
      toast.error('Please select an image file.')
      return
    }
    setIconUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const resp = await postDataWithoutParams('/api/media/file/upload', fd)
      if (resp?.status === 200 && resp?.data?.fileId) {
        handleChange('iconId', resp.data.fileId)
      } else {
        toast.error('Upload failed. Please try again.')
      }
    } catch (err) {
      toast.error('Upload failed. Please try again.')
    } finally {
      setIconUploading(false)
      if (iconFileInputRef.current) iconFileInputRef.current.value = ''
    }
  }

  // Resolved template fields for read-only display when service is template-linked
  const resolvedFromTemplate = hasTemplateId && defaultTemplate
  const templateResolvedFields = resolvedFromTemplate
    ? [
        { label: 'Title', value: defaultTemplate?.title, id: 'tpl-title' },
        {
          label: 'Items Type',
          value: CLINIC_SERVICES_ITEMS_TYPES?.[defaultTemplate?.itemsType]?.title ?? defaultTemplate?.itemsType,
          id: 'tpl-itemsType',
        },
        {
          label: 'Appointment Type',
          value: CLINIC_EVENT_TYPES_FOR_SERVICES.find((t) => t?.id === defaultTemplate?.type)?.title ?? defaultTemplate?.type,
          id: 'tpl-type',
        },
        {
          label: 'Visit Type',
          value: VISIT_EVENT_TYPES_ARRAY.find((t) => t?.id === defaultTemplate?.visitType)?.title ?? defaultTemplate?.visitType,
          id: 'tpl-visitType',
        },
        {
          label: 'Mode',
          value: EVENT_MODES_ARRAY.find((t) => t?.id === defaultTemplate?.mode)?.altTitle ?? defaultTemplate?.mode,
          id: 'tpl-mode',
        },
        {
          label: 'Duration',
          value:
            defaultTemplate?.duration != null || defaultTemplate?.durationInMins != null
              ? `${defaultTemplate?.duration ?? defaultTemplate?.durationInMins ?? ''} mins`
              : null,
          id: 'tpl-duration',
        },
        {
          label: 'Clinical assessments',
          value: null,
          ids:
            defaultTemplate?.clinicalAssessmentIds ??
            defaultTemplate?.psychAssessmentIds ??
            [],
          id: 'tpl-clinicalAssessmentIds',
        },
        ...(defaultTemplate?.iconId || defaultTemplate?.icon
          ? [{ label: 'Icon', value: null, iconId: defaultTemplate?.iconId ?? defaultTemplate?.icon, id: 'tpl-icon' }]
          : []),
      ]
    : []

  const formContent = (
    <Grid container spacing={2}>
          {resolvedFromTemplate && templateResolvedFields.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Resolved from template
                </Typography>
                <Grid container spacing={1.5}>
                  {templateResolvedFields.map((field) => (
                    <Grid item xs={12} sm={6} key={field.id}>
                      {field.ids ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {field.label}
                          </Typography>
                          {field.ids.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              {field.ids.map((id) => (
                                <Chip
                                  key={id}
                                  label={assessmentTitlesMap[id] ?? id}
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </Box>
                      ) : field.iconId ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {field.label}
                          </Typography>
                          <Avatar
                            src={`${mediaURL}${field.iconId}`}
                            variant="rounded"
                            sx={{ width: 40, height: 40, mt: 0.5 }}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {field.label}
                          </Typography>
                          <Typography variant="body2">
                            {field.value ?? '—'}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Icon
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
              <Avatar
                src={formData.iconId ? `${mediaURL}${formData.iconId}` : null}
                variant="rounded"
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              >
                {!formData.iconId && <Image sx={{ color: 'text.secondary' }} />}
              </Avatar>
              <Stack direction="row" spacing={1}>
                <input
                  ref={iconFileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleIconFileChange}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={iconUploading ? null : <CloudUpload />}
                  disabled={iconUploading}
                  onClick={() => iconFileInputRef.current?.click()}
                  sx={{ textTransform: 'none' }}
                >
                  {iconUploading ? 'Uploading…' : 'Upload icon'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PhotoLibrary />}
                  onClick={() => setGalleryOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Choose from gallery
                </Button>
                {formData.iconId && (
                  <IconButton
                    size="small"
                    onClick={() => handleChange('iconId', '')}
                    title="Remove icon"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id="title"
              label="Title"
              required
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              invalid={!formData.title}
              invalidMessage="Title is required"
            />
          </Grid>
          {!!defaultTemplates?.length && (
            <Grid item xs={12}>
              <Autocomplete
                options={defaultTemplates || []}
                getOptionLabel={(option) => option?.title || '—'}
                value={(defaultTemplates || []).find((template) => template?.id === formData.templateId) || null}
                onChange={(_, selectedTemplate) => {
                  setFormData((prev) => ({
                    ...prev,
                    templateId: selectedTemplate?.id || '',
                    title: selectedTemplate?.title ?? prev.title,
                    type: selectedTemplate?.type ?? prev.type,
                    visitType: selectedTemplate?.visitType ?? prev.visitType,
                    mode: selectedTemplate?.mode ?? prev.mode,
                    itemsType: selectedTemplate?.itemsType ?? prev.itemsType,
                    duration:
                      selectedTemplate?.duration ?? selectedTemplate?.durationInMins ?? prev.duration,
                    clinicalAssessmentIds:
                      selectedTemplate?.clinicalAssessmentIds ??
                      selectedTemplate?.psychAssessmentIds ??
                      prev.clinicalAssessmentIds ??
                      [],
                    iconId: selectedTemplate?.iconId ?? selectedTemplate?.icon ?? prev.iconId,
                    targetSpecialtySlug:
                      selectedTemplate?.type === SCREENING_EVENT_TYPE
                        ? normalizeScreeningSpecialtyBucket(selectedTemplate?.targetSpecialtySlug)
                        : '',
                  }))
                }}
                clearOnEscape
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Standard Template"
                    size="small"
                    helperText="Optional. Use this to create another standard clinic service, including screening services for different specialties."
                  />
                )}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <CustomSelect
              label="Items Type"
              required
              options={ITEMS_TYPE_OPTIONS}
              getOptionValue={(optn) => optn?.value}
              getOptionLabel={(optn) => optn?.label}
              selectType={selectTypes.string}
              id="itemType"
              value={formData.itemsType}
              onChangeEvent={(e) => handleChange('itemsType', e.target.value)}
              disabled={hasTemplateId}
            />
          </Grid>
          {!hideAppointmentType && (
            <Grid item xs={12}>
              <CustomSelect
                label="Appointment Type"
                required
                options={CLINIC_EVENT_TYPES_FOR_SERVICES}
                getOptionValue={(optn) => optn?.id}
                getOptionLabel={(optn) => optn?.title}
                selectType={selectTypes.string}
                id="type"
                value={formData.type}
                onChangeEvent={(e) => handleChange('type', e.target.value)}
                disabled={hasTemplateId}
              />
            </Grid>
          )}
          {!hideVisitEventType && (
            <Grid item xs={12}>
              <CustomSelect
                label="Visit Type"
                required
                options={VISIT_EVENT_TYPES_ARRAY}
                getOptionValue={(optn) => optn?.id}
                getOptionLabel={(optn) => optn?.title}
                selectType={selectTypes.string}
                id="visitType"
                value={formData.visitType}
                onChangeEvent={(e) => handleChange('visitType', e.target.value)}
                disabled={hasTemplateId}
              />
            </Grid>
          )}
          {!hideMode && (
            <Grid item xs={12}>
              <CustomSelect
                label="Appointment Mode"
                required
                options={EVENT_MODES_ARRAY}
                getOptionValue={(optn) => optn?.id}
                getOptionLabel={(optn) => optn?.altTitle}
                selectType={selectTypes.string}
                id="mode"
                value={formData.mode}
                onChangeEvent={(e) => handleChange('mode', e.target.value)}
                disabled={hasTemplateId}
              />
            </Grid>
          )}
          {formData.type === SCREENING_EVENT_TYPE && (
            <Grid item xs={12}>
              <Autocomplete
                options={screeningTargetSpecialtyOptions}
                getOptionLabel={(option) => option?.title || formatScreeningSpecialtyLabel(option?.slug)}
                value={
                  screeningTargetSpecialtyOptions.find(
                    (option) =>
                      normalizeScreeningSpecialtyBucket(option?.slug) ===
                      normalizeScreeningSpecialtyBucket(formData.targetSpecialtySlug),
                  ) || screeningTargetSpecialtyOptions[0] || null
                }
                onChange={(_, selectedSpecialty) =>
                  handleChange(
                    'targetSpecialtySlug',
                    normalizeScreeningSpecialtyBucket(selectedSpecialty?.slug),
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Screening Specialty"
                    size="small"
                    helperText="Blank specialty is the psychiatry default screening."
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  normalizeScreeningSpecialtyBucket(option?.slug) ===
                  normalizeScreeningSpecialtyBucket(value?.slug)
                }
              />
            </Grid>
          )}
          {showServiceOwnerDropdown && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={serviceOwnerOptions}
                getOptionLabel={(opt) => opt?.name || opt?.title || opt?.firstName || '—'}
                value={
                  (formData.specializationsMentorIds || []).map((id) =>
                    serviceOwnerOptions.find(
                      (p) => (p?.id || p?.uid) === id || String(p?.id || p?.uid) === String(id),
                    ),
                  ).filter(Boolean)
                }
                onChange={(_, selected) => {
                  handleChange(
                    'specializationsMentorIds',
                    (selected || []).map((p) => p?.id || p?.uid).filter(Boolean),
                  )
                }}
                disabled={loadingPractitioners}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Service Owner"
                    placeholder="Select practitioners…"
                    size="small"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option?.id ?? option?.uid ?? index}
                      label={option?.name || option?.title || option?.firstName || '—'}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) =>
                  (option?.id || option?.uid) === (value?.id || value?.uid)
                }
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <CustomInput
              id="duration"
              label="Duration (minutes)"
              value={formData.duration ?? ''}
              onChange={(e) => {
                const v = e.target.value
                const num = v === '' ? '' : Math.max(0, parseInt(v, 10) || 0)
                handleChange('duration', num)
              }}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              helperText="Slot duration for this appointment type"
            />
          </Grid>
          {(formData.clinicalAssessmentIds?.length > 0 || formData.psychAssessmentIds?.length > 0) && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Clinical assessments
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {(formData.clinicalAssessmentIds || formData.psychAssessmentIds || []).map((id) => (
                  <Chip
                    key={id}
                    label={assessmentTitlesMap[id] ?? id}
                    size="small"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Stack>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                Pricing
              </FormLabel>
              {showPricingSourceSelector ? (
                <RadioGroup
                  row
                  value={pricingSource}
                  onChange={(e) => setPricingSource(e.target.value)}
                  sx={{ flexWrap: 'wrap', gap: 0.5 }}
                >
                  {templatePricingTiers.map((tier) => {
                    const pd = tier.data
                    const sym = currency_list?.find((c) => c?.code === pd?.currencyId)?.symbol || pd?.currencyId || ''
                    const priceLabel = [pd.price, pd.discountedPrice, pd.introductoryPrice].filter((v) => isNumericValue(v)).length
                      ? `${sym}${pd.price ?? pd.discountedPrice ?? pd.introductoryPrice ?? '—'}`
                      : '—'
                    return (
                      <FormControlLabel
                        key={tier.key}
                        value={tier.key}
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            {tier.label} — {priceLabel}
                          </Typography>
                        }
                      />
                    )
                  })}
                  <FormControlLabel
                    value={PRICING_SOURCE_CUSTOM}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Custom pricing (enter below)</Typography>}
                  />
                </RadioGroup>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Enter custom pricing below.
                </Typography>
              )}
              {(!showPricingSourceSelector || pricingSource === PRICING_SOURCE_CUSTOM) && (
                <ChangeCountryPricing
                  handleChange={handleChangePricing}
                  priceObject={formData?.pricingDetail || {}}
                />
              )}
            </FormControl>
          </Grid>
        </Grid>
  )

  const galleryDialog = (
    <Dialog open={galleryOpen} onClose={() => setGalleryOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Choose icon from gallery</DialogTitle>
      <DialogContent>
        <Grid container spacing={1.5} sx={{ pt: 1 }}>
          <Grid item>
            <Box
              onClick={() => {
                handleChange('iconId', '')
                setGalleryOpen(false)
              }}
              sx={{
                width: 72,
                height: 72,
                borderRadius: 2,
                border: '2px solid',
                borderColor: !formData.iconId ? 'primary.main' : 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                No icon
              </Typography>
            </Box>
          </Grid>
          {SERVICE_ICON_GALLERY.map((item) => (
            <Grid item key={item.id}>
              <Box
                onClick={() => {
                  handleChange('iconId', item.id)
                  setGalleryOpen(false)
                }}
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: formData.iconId === item.id ? 'primary.main' : 'divider',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <Avatar
                  src={item.url ? item.url : `${mediaURL}${item.id}`}
                  variant="rounded"
                  sx={{ width: '100%', height: '100%' }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
        {SERVICE_ICON_GALLERY.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No predefined icons. Upload an icon using the upload button.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setGalleryOpen(false)} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )

  if (isDrawerMode) {
    return (
      <>
        {formContent}
        {galleryDialog}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={closeDialog} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px', px: 3 }}>
              Cancel
            </Button>
            <Button
              disabled={!!saving}
              onClick={handleSubmit}
              variant="contained"
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.95) 0%, rgba(22, 163, 74, 0.95) 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 1) 0%, rgba(22, 163, 74, 1) 100%)',
                },
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </>
    )
  }

  return (
    <>
      <DialogTitle>{clinicAppointmentType ? 'Edit' : 'Add'} Clinic Appointment Type</DialogTitle>
      <DialogContent>{formContent}</DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} color="unFocused">
          Cancel
        </Button>
        <Button disabled={!!saving} onClick={handleSubmit} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
      {galleryDialog}
    </>
  )
}

function ChangeCountryPricing({
  priceObject,
  handleChange,
  pricingMatrix,
  handleDelete,
  dialogContentRef,
}) {
  //   const { unSecureSetupData } = useContext(UnsecureSetupContext)
  //   const isRestOfWorld = priceObject?.countryId === 'row'
  const isRestOfWorld = false
  const [showConfirmation, setShowConfirmation] = useState(false)

  //   function changeCountry(selectValue) {
  //     const countryCode = selectValue?.id
  //     const currencyCode = selectValue?.currency
  //     console.log({ selectValue })
  //     handleChange({ target: { value: countryCode, id: 'countryId' } })
  //     handleChange({ target: { value: currencyCode, id: 'currencyId' } })
  //   }

  //   function deletePrice() {
  //     setShowConfirmation(false)
  //     handleDelete()
  //   }

  //   useEffect(() => {
  //     const dialogContent = dialogContentRef?.current
  //     console.log(dialogContent?.scrollHeight)
  //     if (dialogContent) {
  //       dialogContent.scrollTo({
  //         top: dialogContent.scrollHeight,
  //         behavior: 'smooth',
  //       })
  //     }
  //   }, [])

  return (
    <div
      style={{
        border: '1px solid #00000033',
        borderRadius: '7px',
        padding: '20px',
        boxShadow: isRestOfWorld ? '0px 0px 5px #900aff' : 'unset',
        position: 'relative',
      }}
    >
      <CountryPricingContainer container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomSelect
            id="currencyId"
            value={priceObject?.currencyId}
            options={currency_list?.map((currency) => ({
              ...currency,
              name: `(${currency.symbol}) ${currency.name}`,
            }))}
            customComponent={StyledEventReactSelect}
            getOptionValue={(option) => option?.code}
            getOptionLabel={(option) => option.name}
            selectType={selectTypes.string}
            menuPlacement="auto"
            label="Currency"
            onChangeEvent={handleChange}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            menuPortalTarget={document.body}
            invalid={!priceObject?.currencyId}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomInput
            id="price"
            value={priceObject?.price}
            label="Price"
            required
            inputAndMessageContainer={StyledEventInputContainer}
            type="number"
            invalid={!priceObject?.price}
            onChange={(e) => {
              const newValue = Math.max(0, e.target.value) // Ensure value is not below 0
              handleChange({
                target: {
                  value: e.target.value === '' ? '' : newValue,
                  id: 'price',
                  type: 'number',
                },
              })
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomInput
            id="discountedPrice"
            value={priceObject?.discountedPrice}
            label="Discounted Price"
            onFocus={(e) => {
              if (e.target.value === '' || e.target.value === null || e.target.value === undefined)
                handleChange({
                  target: { value: priceObject?.price, id: 'discountedPrice', type: 'number' },
                })
            }}
            inputAndMessageContainer={StyledEventInputContainer}
            placeholder={priceObject?.price}
            type="number"
            onChange={(e) => {
              const newValue = Math.max(0, e.target.value) // Ensure value is not below 0
              handleChange({
                target: {
                  value: e.target.value === '' ? '' : newValue,
                  id: 'discountedPrice',
                  type: 'number',
                },
              })
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomInput
            id="introductoryPrice"
            value={priceObject?.introductoryPrice}
            label="Introductory Price"
            inputAndMessageContainer={StyledEventInputContainer}
            placeholder={priceObject?.price}
            onFocus={(e) => {
              if (e.target.value === '' || e.target.value === null || e.target.value === undefined)
                handleChange({
                  target: { value: priceObject?.price, id: 'introductoryPrice', type: 'number' },
                })
            }}
            type="number"
            onChange={(e) => {
              const newValue = Math.max(0, e.target.value) // Ensure value is not below 0
              handleChange({
                target: {
                  value: e.target.value === '' ? '' : newValue,
                  id: 'introductoryPrice',
                  type: 'number',
                },
              })
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomInput
            id="vatPercentage"
            value={priceObject?.vatPercentage ?? ''}
            label="GST %"
            inputAndMessageContainer={StyledEventInputContainer}
            type="number"
            onChange={(e) => {
              const newValue = Math.max(0, e.target.value) // Ensure value is not below 0
              handleChange({
                target: {
                  value: e.target.value === '' ? '' : newValue,
                  id: 'vatPercentage',
                  type: 'number',
                },
              })
            }}
          />
        </Grid>
      </CountryPricingContainer>
    </div>
  )
}
